import React, { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { History, ArrowForward } from '@material-ui/icons';
import { ModalDefaultDraggable } from '../../../molecules';
import { Loader } from '../../../atoms';
import { JurisdictionAuditEntry, useGetJurisdictionAuditLogQuery } from '../../../redux/rtkQuery/apiWikiSlice';

type Props = {
  jurisdiction: { id: number; jurisdictions?: string; county?: string; state?: string };
  onClose: () => void;
};

const ACTION_LABELS: Record<JurisdictionAuditEntry['action'], string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

// Dates are formatted with toLocaleString and plain math rather than date-fns:
// formatDistanceToNow threw "Invalid time value" in the Parcel production
// build (white page on dev) even though the same timestamps worked locally.
const toDate = (timestamp: string) => {
  // Django sends microseconds (.722019); trim to milliseconds for Safari.
  const date = new Date(String(timestamp || '').replace(/(\.\d{3})\d+/, '$1'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (date: Date | null) =>
  date
    ? date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';

const RELATIVE_UNITS: [string, number][] = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

const formatRelative = (date: Date | null) => {
  if (!date) return '';
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const unit = RELATIVE_UNITS.find(([, size]) => Math.abs(seconds) >= size);
  if (!unit) return 'just now';
  const count = Math.round(Math.abs(seconds) / unit[1]);
  const label = `${count} ${unit[0]}${count === 1 ? '' : 's'}`;
  return seconds >= 0 ? `${label} ago` : `in ${label}`;
};

const formatValue = (field: string, value: string | null) => {
  if (value === null || value === undefined) return null;
  if (field === 'is_deleted') return value === 'True' ? 'Yes' : 'No';
  return value;
};

const initials = (name?: string) =>
  (name || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

const JurisdictionAuditLog: FC<Props> = ({ jurisdiction, onClose }) => {
  const [fieldFilter, setFieldFilter] = useState<string>('all');
  const { data = [], isFetching, isError } = useGetJurisdictionAuditLogQuery(jurisdiction.id);

  const fieldOptions = useMemo(() => {
    const seen = new Map<string, string>();
    data.forEach(entry => entry.changes.forEach(c => seen.set(c.field, c.label)));
    return Array.from(seen, ([field, label]) => ({ field, label }));
  }, [data]);

  const entries = useMemo(
    () =>
      fieldFilter === 'all'
        ? data
        : data
            .map(entry => ({
              ...entry,
              changes: entry.changes.filter(c => c.field === fieldFilter),
            }))
            .filter(entry => entry.changes.length > 0),
    [data, fieldFilter],
  );

  const lastEntry = data[0];
  const location = [jurisdiction.county && `${jurisdiction.county} County`, jurisdiction.state].filter(Boolean).join(', ');

  return (
    <ModalDefaultDraggable title="Audit Log" onClose={onClose} wide centered>
      <Root>
        <SummaryCard>
          <SummaryCol>
            <Eyebrow>Jurisdiction</Eyebrow>
            <SummaryTitle>{jurisdiction.jurisdictions || '—'}</SummaryTitle>
            {location && <SummaryMeta>{location}</SummaryMeta>}
          </SummaryCol>
          <Divider />
          <SummaryCol>
            <Eyebrow>Last Modified</Eyebrow>
            {lastEntry ? (
              <>
                <SummaryTitle>{lastEntry.actor?.name || 'System'}</SummaryTitle>
                <SummaryMeta>{formatDateTime(toDate(lastEntry.timestamp))}</SummaryMeta>
              </>
            ) : (
              <SummaryMeta>{isFetching ? 'Loading…' : 'No recorded changes'}</SummaryMeta>
            )}
          </SummaryCol>
        </SummaryCard>

        <Toolbar>
          <SectionEyebrow>
            Change History
            {!isFetching && <CountPill>{entries.length}</CountPill>}
          </SectionEyebrow>
          {fieldOptions.length > 1 && (
            <FilterSelect value={fieldFilter} onChange={e => setFieldFilter(e.target.value)} aria-label="Filter by field">
              <option value="all">All fields</option>
              {fieldOptions.map(({ field, label }) => (
                <option key={field} value={field}>
                  {label}
                </option>
              ))}
            </FilterSelect>
          )}
        </Toolbar>

        {isFetching && (
          <StateBox>
            <Loader size={32} thickness={3.5} />
            <span>Loading audit log…</span>
          </StateBox>
        )}

        {!isFetching && isError && (
          <ErrorCallout>Couldn&apos;t load the audit log. Please try again.</ErrorCallout>
        )}

        {!isFetching && !isError && entries.length === 0 && (
          <StateBox>
            <EmptyIcon>
              <History />
            </EmptyIcon>
            <EmptyTitle>No changes recorded yet</EmptyTitle>
            <span>
              Edits to key fields of this jurisdiction will appear here with who made them and when.
            </span>
          </StateBox>
        )}

        {!isFetching && !isError && entries.length > 0 && (
          <Timeline>
            {entries.map(entry => {
              const date = toDate(entry.timestamp);
              return (
                <TimelineItem key={entry.id}>
                  <Dot $action={entry.action} />
                  <EntryCard>
                    <EntryHeader>
                      <Avatar>{initials(entry.actor?.name)}</Avatar>
                      <Who>
                        <ActorName>{entry.actor?.name || 'System'}</ActorName>
                        {entry.actor?.email && entry.actor.email !== entry.actor.name && (
                          <ActorEmail>{entry.actor.email}</ActorEmail>
                        )}
                      </Who>
                      <ActionBadge $action={entry.action}>
                        {ACTION_LABELS[entry.action]}
                      </ActionBadge>
                      <When title={date ? date.toString() : undefined}>
                        <span>{formatDateTime(date)}</span>
                        <Relative>{formatRelative(date)}</Relative>
                      </When>
                    </EntryHeader>

                    {entry.changes.length > 0 && (
                      <Changes>
                        {entry.changes.map(change => {
                          const oldValue = formatValue(change.field, change.old);
                          const newValue = formatValue(change.field, change.new);
                          return (
                            <ChangeRow key={change.field} $key={change.field === 'jurisdictions'}>
                              <FieldLabel>{change.label}</FieldLabel>
                              <Values>
                                {entry.action !== 'create' && (
                                  <>
                                    <OldValue $empty={!oldValue}>{oldValue || 'Empty'}</OldValue>
                                    <Arrow>
                                      <ArrowForward />
                                    </Arrow>
                                  </>
                                )}
                                <NewValue $empty={!newValue}>{newValue || 'Empty'}</NewValue>
                              </Values>
                            </ChangeRow>
                          );
                        })}
                      </Changes>
                    )}
                  </EntryCard>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Root>
    </ModalDefaultDraggable>
  );
};

export default JurisdictionAuditLog;

type ActionProps = { $action: JurisdictionAuditEntry['action'] };

const actionTone = ({ $action }: ActionProps) =>
  ({
    create: { bg: 'var(--success-50)', fg: 'var(--success-700)', border: 'var(--success-100)', dot: 'var(--success-500)' },
    update: { bg: 'var(--primary-50)', fg: 'var(--primary-700)', border: 'var(--primary-100)', dot: 'var(--primary-600)' },
    delete: { bg: 'var(--danger-50)', fg: 'var(--danger-700)', border: 'var(--danger-100)', dot: 'var(--danger-500)' },
  }[$action]);

const Root = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  color: var(--mainText);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 20px;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const SummaryCol = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Divider = styled.div`
  background: var(--border);

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const SummaryTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SummaryMeta = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SectionEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const CountPill = styled.span`
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.6875rem;
  letter-spacing: 0;
  box-sizing: border-box;
`;

const FilterSelect = styled.select`
  height: 36px;
  min-width: 200px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const EmptyIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--neutral-100);
  color: var(--neutral-500);
`;

const EmptyTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-700);
`;

const ErrorCallout = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--danger-50);
  border: 1px solid var(--danger-100);
  color: var(--danger-700);
  font-size: 0.875rem;
`;

const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0 0 0 22px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: var(--neutral-200);
  }
`;

const TimelineItem = styled.li`
  position: relative;
`;

const Dot = styled.span<ActionProps>`
  position: absolute;
  left: -22px;
  top: 20px;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: ${p => actionTone(p).dot};
  border: 3px solid var(--neutral-0);
  box-shadow: 0 0 0 1px var(--border);
  box-sizing: border-box;
`;

const EntryCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const EntryHeader = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 36px minmax(0, 1fr) auto;
  }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.8125rem;
  font-weight: 700;
  box-sizing: border-box;
`;

const Who = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ActorName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-800);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActorEmail = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionBadge = styled.span<ActionProps>`
  height: 22px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p => actionTone(p).bg};
  color: ${p => actionTone(p).fg};
  border: 1px solid ${p => actionTone(p).border};
  box-sizing: border-box;
`;

const When = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  white-space: nowrap;

  @media only screen and (max-width: 640px) {
    grid-column: 2 / -1;
    align-items: flex-start;
  }
`;

const Relative = styled.span`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const Changes = styled.div`
  border-top: 1px solid var(--border);
  background: var(--neutral-50);
`;

const ChangeRow = styled.div<{ $key: boolean }>`
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
  box-shadow: ${({ $key }) => ($key ? 'inset 3px 0 0 var(--warning-500)' : 'none')};

  &:last-child {
    border-bottom: 0;
  }

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const FieldLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--neutral-600);
`;

const Values = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const ValueChip = styled.span<{ $empty: boolean }>`
  max-width: 100%;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.8125rem;
  line-height: 1.4;
  word-break: break-word;
  font-style: ${({ $empty }) => ($empty ? 'italic' : 'normal')};
`;

const OldValue = styled(ValueChip)`
  background: var(--danger-50);
  color: ${({ $empty }) => ($empty ? 'var(--neutral-400)' : 'var(--danger-700)')};
  text-decoration: ${({ $empty }) => ($empty ? 'none' : 'line-through')};
`;

const NewValue = styled(ValueChip)`
  background: var(--success-50);
  color: ${({ $empty }) => ($empty ? 'var(--neutral-400)' : 'var(--success-700)')};
  font-weight: ${({ $empty }) => ($empty ? 400 : 600)};
`;

const Arrow = styled.span`
  display: inline-flex;
  color: var(--neutral-400);

  svg {
    font-size: 16px;
  }
`;
